import { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Divider, Button } from '@mui/material';
import { motion } from 'framer-motion';
import QRCode from 'qrcode.react';
import { InfoOutlined, AccessTime } from '@mui/icons-material';
import PropTypes from 'prop-types';

const UpiPaymentQR = ({ intentDetails, handleCloseModal }) => {
    const upiUrl = intentDetails?.url;
    const [timeLeft, setTimeLeft] = useState(5 * 60); // 5 minutes in seconds
    const [isExpired, setIsExpired] = useState(false);
    useEffect(() => {
        if (!timeLeft) {
            handleCloseModal();
            setIsExpired(true);
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeLeft]);

    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;

    return (
        <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                py: 6,
                px: 3,
                background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(245,248,255,0.6))',
                borderRadius: 4,
                boxShadow: '0 8px 25px rgba(0,0,0,0.08), inset 0 0 20px rgba(255,255,255,0.4)'
            }}
        >
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', mb: 1 }}>
                Scan to Complete Your Payment
            </Typography>

            <Typography variant="body1" sx={{ maxWidth: 600, color: 'text.secondary', mb: 3 }}>
                Please scan the QR code below using your preferred UPI app (such as GPay, PhonePe, or Paytm). Do not refresh or close this
                page until the transaction is completed.
            </Typography>

            <Card
                component={motion.div}
                whileHover={!isExpired ? { scale: 1.03 } : {}}
                transition={{ type: 'spring', stiffness: 200 }}
                sx={{
                    borderRadius: 3,
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    p: 4,
                    background: '#ffffffd9',
                    opacity: isExpired ? 0.4 : 1,
                    pointerEvents: isExpired ? 'none' : 'auto'
                }}
            >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <QRCode value={upiUrl || ''} size={220} includeMargin={true} />
                    <Divider sx={{ my: 2, width: '100px' }} />
                    <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600 }}>
                        Amount: ₹{intentDetails?.amount || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Beneficiary: {intentDetails?.name || 'YesPay - Sruthi'}
                    </Typography>
                </CardContent>
            </Card>

            {/* Countdown Timer */}
            {!isExpired ? (
                <Box mt={3} display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ color: '#1976d2', fontWeight: 600 }}>
                    <AccessTime fontSize="small" />
                    <Typography variant="body2">
                        QR expires in {minutes}:{seconds.toString().padStart(2, '0')}
                    </Typography>
                </Box>
            ) : (
                <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} mt={3} textAlign="center">
                    <Typography variant="h6" color="error" fontWeight={600}>
                        QR Code Expired
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                        Please refresh the page or generate a new payment request.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2, textTransform: 'none' }}
                        onClick={() => window.location.reload()}
                    >
                        Refresh QR
                    </Button>
                </Box>
            )}

            <Box mt={3} display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ color: 'text.secondary' }}>
                <InfoOutlined fontSize="small" />
                <Typography variant="body2">You’ll be redirected automatically once the payment is verified.</Typography>
            </Box>
        </Box>
    );
};

UpiPaymentQR.propTypes = {
    intentDetails: PropTypes.object,
    handleCloseModal: PropTypes.func
};

export default UpiPaymentQR;
